import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { supabase } from '@/integrations/supabase/client'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  UserCircle, Settings, IdCard, LogOut, Sun, Moon, Monitor, Award,
} from 'lucide-react'

export function PortalAccountMenu({ basePath, orgSlug }: { basePath: string; orgSlug: string | null }) {
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const [open, setOpen] = useState(false)
  const [confirmSignOut, setConfirmSignOut] = useState(false)

  const { data: profile } = useQuery({
    queryKey: ['portal-account-menu'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_profiles')
        .select('full_name, account_type, avatar_url')
        .eq('id', user.id)
        .single() as any)
      return data as { full_name: string; account_type: string; avatar_url: string | null } | null
    },
  })

  const { data: score } = useQuery({
    queryKey: ['portal-account-menu-score'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_coverage_scores' as any)
        .select('score_total, badge_tier')
        .eq('journalist_id', user.id)
        .maybeSingle() as any)
      return data as { score_total: number; badge_tier: string } | null
    },
  })

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate(orgSlug ? `/orgs/${orgSlug}/login` : '/portal/login', { replace: true })
  }

  const initials = (profile?.full_name ?? '?')
    .split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className="h-9 w-9 rounded-full overflow-hidden border border-border shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Account menu"
          >
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-[var(--portal-accent)]/15 text-[var(--portal-accent)] flex items-center justify-center text-xs font-bold">
                {initials}
              </div>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="flex items-center gap-3 py-2">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[var(--portal-accent)]/15 text-[var(--portal-accent)] flex items-center justify-center text-sm font-bold shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{profile?.full_name ?? 'Your account'}</p>
              <p className="text-xs text-muted-foreground capitalize truncate">{profile?.account_type ?? 'journalist'}</p>
              {score && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Award className="w-3 h-3" /> {Math.round(score.score_total)} pts · {score.badge_tier === 'none' ? 'Unranked' : score.badge_tier}
                </p>
              )}
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <NavLink to={`${basePath}/profile`} className="flex items-center gap-2 cursor-pointer">
              <UserCircle className="w-4 h-4" /> View profile
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <NavLink to={`${basePath}/passes`} className="flex items-center gap-2 cursor-pointer">
              <IdCard className="w-4 h-4" /> My Passes
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <NavLink to={`${basePath}/settings`} className="flex items-center gap-2 cursor-pointer">
              <Settings className="w-4 h-4" /> Settings
            </NavLink>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <div className="px-2 py-1.5">
            <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Theme</p>
            <div className="flex gap-1">
              {[
                { value: 'light', icon: Sun, label: 'Light' },
                { value: 'dark', icon: Moon, label: 'Dark' },
                { value: 'system', icon: Monitor, label: 'System' },
              ].map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={`flex-1 flex flex-col items-center gap-1 py-1.5 rounded-md text-[10px] transition-colors ${
                    theme === value ? 'bg-[var(--portal-accent)]/10 text-[var(--portal-accent)]' : 'text-muted-foreground hover:bg-muted'
                  }`}
                  aria-label={label}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
            onSelect={(e) => { e.preventDefault(); setOpen(false); setConfirmSignOut(true) }}
          >
            <LogOut className="w-4 h-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmSignOut} onOpenChange={setConfirmSignOut}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out?</AlertDialogTitle>
            <AlertDialogDescription>You'll need to sign back in to access your journalist portal.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut}>Sign out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
